/**
 * Firebase Analytics Utility
 *
 * Centralized analytics tracking for Its Your Story platform.
 * All analytics events should be called through these helper functions
 * to ensure consistent event naming and payload structure.
 *
 * @see ../../../docs/analytics/ANALYTICS_EVENTS.md for full event taxonomy documentation
 */

import {
  logEvent,
  setUserProperties as firebaseSetUserProperties,
  setUserId as firebaseSetUserId,
} from "firebase/analytics";
import { analytics } from "../firebase";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type ContentType = "story" | "chapter" | "blog" | "profile" | "comment";
export type SharePlatform = "whatsapp" | "twitter" | "facebook" | "copy_link";
export type AuthMethod = "google" | "email";
export type Genre =
  | "romance"
  | "thriller"
  | "fantasy"
  | "horror"
  | "comedy"
  | "drama"
  | "sci-fi"
  | "mystery"
  | "other";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Safely log an event to Firebase Analytics
 * Handles cases where analytics might not be initialized
 */
const safeLogEvent = (
  eventName: string,
  eventParams?: Record<string, unknown>
) => {
  if (analytics) {
    logEvent(analytics, eventName, eventParams);
  }
};

// ============================================================================
// INITIALIZATION (for backward compatibility)
// ============================================================================

/**
 * Initialize Google Analytics
 * Note: With Firebase Analytics, initialization happens automatically
 * This function is kept for backward compatibility
 */
export const initializeGA = () => {
  // Firebase Analytics is initialized automatically in firebase.ts
  // This function is kept for backward compatibility
};

// ============================================================================
// USER IDENTIFICATION
// ============================================================================

/**
 * Set the user ID for analytics tracking
 * Call this after user logs in
 */
export const setAnalyticsUserId = (userId: string) => {
  if (analytics) {
    firebaseSetUserId(analytics, userId);
  }
};

/**
 * Alias for setAnalyticsUserId (backward compatibility)
 */
export const setUserId = setAnalyticsUserId;

/**
 * Set user properties for segmentation
 * Call this after user logs in or updates profile
 */
export const setAnalyticsUserProperties = (
  properties: Record<string, string | number | boolean | undefined>
) => {
  if (analytics) {
    // Filter out undefined values
    const cleanProperties = Object.fromEntries(
      Object.entries(properties).filter(([, v]) => v !== undefined)
    );
    firebaseSetUserProperties(analytics, cleanProperties);
  }
};

/**
 * Alias for setAnalyticsUserProperties (backward compatibility)
 */
export const setUserProperties = setAnalyticsUserProperties;

// ============================================================================
// PAGE VIEW EVENTS
// ============================================================================

/**
 * Track page views
 * Automatically called by router, but can be called manually if needed
 */
export const trackPageView = (pageName: string, pageTitle?: string) => {
  safeLogEvent("page_view", {
    page_name: pageName,
    page_title: pageTitle || pageName,
    page_location: typeof window !== "undefined" ? window.location.href : "",
  });
};

// ============================================================================
// AUTHENTICATION EVENTS (Object-based API)
// ============================================================================

/**
 * Authentication tracking object
 */
export const trackAuth = {
  /**
   * Track user login
   */
  login: (method: AuthMethod) => {
    safeLogEvent("login", { method });
  },

  /**
   * Track user signup
   */
  signup: (method: AuthMethod) => {
    safeLogEvent("sign_up", { method });
  },

  /**
   * Track first time user
   */
  firstTimeUser: () => {
    safeLogEvent("first_time_user");
  },

  /**
   * Track user logout
   */
  logout: () => {
    safeLogEvent("logout");
  },
};

// Function-based aliases for backward compatibility
export const trackLogin = trackAuth.login;
export const trackSignup = trackAuth.signup;
export const trackLogout = trackAuth.logout;

// ============================================================================
// STORY EVENTS (Object-based API)
// ============================================================================

/**
 * Story tracking object
 */
export const trackStory = {
  /**
   * Track when a user views a story page
   */
  viewed: (
    storyId: string,
    authorId: string,
    storyTitle: string,
    genre?: Genre,
    language?: "en" | "hi"
  ) => {
    safeLogEvent("story_view", {
      story_id: storyId,
      author_id: authorId,
      story_title: storyTitle,
      genre: genre || "other",
      language: language || "en",
    });
  },

  /**
   * Track when a story is created (supports multiple signatures)
   */
  created: (
    storyId: string,
    categoryOrGenre: string,
    languageOrIsFirstStory?: string | boolean
  ) => {
    safeLogEvent("story_create", {
      story_id: storyId,
      category: categoryOrGenre,
      is_first_story:
        typeof languageOrIsFirstStory === "boolean"
          ? languageOrIsFirstStory
          : undefined,
      language:
        typeof languageOrIsFirstStory === "string"
          ? languageOrIsFirstStory
          : "en",
    });
  },

  /**
   * Track category selection
   */
  categorySelected: (category: string) => {
    safeLogEvent("category_selected", { category });
  },

  /**
   * Track story published (supports chapterCount as number or category as string)
   */
  published: (storyId: string, chapterCountOrCategory: number | string) => {
    if (typeof chapterCountOrCategory === "number") {
      safeLogEvent("story_published", {
        story_id: storyId,
        chapter_count: chapterCountOrCategory,
      });
    } else {
      safeLogEvent("story_published", {
        story_id: storyId,
        category: chapterCountOrCategory,
      });
    }
  },

  /**
   * Track story deleted
   */
  deleted: (storyId: string) => {
    safeLogEvent("story_deleted", { story_id: storyId });
  },
};

// Function-based aliases
export const trackStoryView = trackStory.viewed;
export const trackStoryCreate = trackStory.created;

// ============================================================================
// READING EVENTS (Object-based API)
// ============================================================================

/**
 * Reading tracking object
 */
export const trackReading = {
  /**
   * Track when a chapter is viewed
   */
  chapterViewed: (
    storyId: string,
    chapterId: string,
    chapterNumber: number,
    totalChapters: number
  ) => {
    safeLogEvent("chapter_view", {
      story_id: storyId,
      chapter_id: chapterId,
      chapter_number: chapterNumber,
      total_chapters: totalChapters,
    });
  },

  /**
   * Track when a story is viewed
   */
  storyViewed: (
    storyId: string,
    category: string | null,
    authorId: string
  ) => {
    safeLogEvent("story_view", {
      story_id: storyId,
      category,
      author_id: authorId,
    });
  },

  /**
   * Track when reading starts
   */
  readingStarted: (storyId: string) => {
    safeLogEvent("reading_started", { story_id: storyId });
  },

  /**
   * Track when reading continues to next chapter
   */
  readingContinued: (storyId: string, chapterNumber: number) => {
    safeLogEvent("reading_continued", {
      story_id: storyId,
      chapter_number: chapterNumber,
    });
  },

  /**
   * Track when all chapters are read
   */
  readingCompleted: (storyId: string, totalChapters: number) => {
    safeLogEvent("reading_completed", {
      story_id: storyId,
      total_chapters: totalChapters,
    });
  },

  /**
   * Track chapter start
   */
  chapterStarted: (
    storyId: string,
    chapterId: string,
    chapterNumber: number
  ) => {
    safeLogEvent("chapter_start", {
      story_id: storyId,
      chapter_id: chapterId,
      chapter_number: chapterNumber,
    });
  },

  /**
   * Track chapter completion
   */
  chapterCompleted: (
    storyId: string,
    chapterId: string,
    chapterNumber: number,
    readTimeSeconds: number
  ) => {
    safeLogEvent("chapter_complete", {
      story_id: storyId,
      chapter_id: chapterId,
      chapter_number: chapterNumber,
      read_time_seconds: readTimeSeconds,
    });
  },

  /**
   * Track scroll depth (can be called with 2 or 3 args)
   */
  scrollDepth: (
    storyId: string,
    chapterIdOrDepth: string | number,
    depthPercent?: 25 | 50 | 75 | 100
  ) => {
    // Support both (storyId, chapterId, depth) and (storyId, depth) signatures
    if (typeof chapterIdOrDepth === "number") {
      safeLogEvent("scroll_depth", {
        story_id: storyId,
        depth_percent: chapterIdOrDepth,
      });
    } else {
      safeLogEvent("scroll_depth", {
        story_id: storyId,
        chapter_id: chapterIdOrDepth,
        depth_percent: depthPercent,
      });
    }
  },

  /**
   * Track time spent on story/chapter
   */
  timeOnStory: (storyId: string, timeSeconds: number) => {
    safeLogEvent("time_on_story", {
      story_id: storyId,
      time_seconds: timeSeconds,
    });
  },
};

// Function-based aliases
export const trackChapterStart = trackReading.chapterStarted;
export const trackChapterComplete = trackReading.chapterCompleted;
export const trackScrollDepth = trackReading.scrollDepth;

// ============================================================================
// WRITING EVENTS (Object-based API)
// ============================================================================

/**
 * Writing tracking object
 */
export const trackWriting = {
  /**
   * Track writing session started (can be called with or without storyId)
   */
  sessionStarted: (storyId?: string) => {
    safeLogEvent("writing_session_started", {
      story_id: storyId,
    });
  },

  /**
   * Track writing session ended
   */
  sessionEnded: (durationMinutes: number, wordsWritten: number) => {
    safeLogEvent("writing_session_ended", {
      duration_minutes: durationMinutes,
      words_written: wordsWritten,
    });
  },

  /**
   * Track writing session completed
   */
  sessionCompleted: (storyId: string, action: string) => {
    safeLogEvent("writing_session_completed", {
      story_id: storyId,
      action,
    });
  },

  /**
   * Track chapter created
   */
  chapterCreated: (
    storyId: string,
    chapterId: string,
    chapterNumber: number
  ) => {
    safeLogEvent("chapter_created", {
      story_id: storyId,
      chapter_id: chapterId,
      chapter_number: chapterNumber,
    });
  },

  /**
   * Track chapter published
   */
  chapterPublished: (
    storyId: string,
    chapterId: string,
    chapterNumber: number,
    wordCount: number,
    isFirstChapter: boolean
  ) => {
    safeLogEvent("chapter_publish", {
      story_id: storyId,
      chapter_id: chapterId,
      chapter_number: chapterNumber,
      word_count: wordCount,
      is_first_chapter: isFirstChapter,
    });
  },

  /**
   * Track chapter saved as draft
   */
  chapterDraftSaved: (storyId: string, chapterId: string, wordCount: number) => {
    safeLogEvent("chapter_draft_saved", {
      story_id: storyId,
      chapter_id: chapterId,
      word_count: wordCount,
    });
  },

  /**
   * Track chapter deleted
   */
  chapterDeleted: (storyId: string, chapterId: string) => {
    safeLogEvent("chapter_deleted", {
      story_id: storyId,
      chapter_id: chapterId,
    });
  },

  /**
   * Track story continued (adding more chapters)
   */
  storyContinued: (storyId: string, chapterNumber: number) => {
    safeLogEvent("story_continued", {
      story_id: storyId,
      chapter_number: chapterNumber,
    });
  },

  /**
   * Track writing streak
   */
  streakUpdated: (streakDays: number, isMilestone: boolean = false) => {
    safeLogEvent("writing_streak", {
      streak_days: streakDays,
      is_milestone: isMilestone,
    });
  },
};

// Function-based aliases
export const trackChapterPublish = trackWriting.chapterPublished;
export const trackWritingStreak = trackWriting.streakUpdated;

// ============================================================================
// ENGAGEMENT EVENTS (Object-based API)
// ============================================================================

/**
 * Engagement tracking object
 */
export const trackEngagement = {
  /**
   * Track when a user follows someone
   */
  userFollowed: (targetUserId: string, targetUserName?: string) => {
    safeLogEvent("follow", {
      target_user_id: targetUserId,
      target_user_name: targetUserName,
    });
  },

  /**
   * Track when a user unfollows someone
   */
  userUnfollowed: (targetUserId: string) => {
    safeLogEvent("unfollow", { target_user_id: targetUserId });
  },

  /**
   * Track when a user likes content
   */
  contentLiked: (contentType: ContentType, contentId: string) => {
    safeLogEvent("like", {
      content_type: contentType,
      content_id: contentId,
    });
  },

  /**
   * Track when a user unlikes content
   */
  contentUnliked: (contentType: ContentType, contentId: string) => {
    safeLogEvent("unlike", {
      content_type: contentType,
      content_id: contentId,
    });
  },

  /**
   * Track when a story is liked
   */
  storyLiked: (storyId: string, authorId?: string) => {
    safeLogEvent("story_liked", {
      story_id: storyId,
      author_id: authorId,
    });
  },

  /**
   * Track when a story is unliked
   */
  storyUnliked: (storyId: string, authorId?: string) => {
    safeLogEvent("story_unliked", {
      story_id: storyId,
      author_id: authorId,
    });
  },

  /**
   * Track when a user posts a comment
   */
  commentPosted: (
    storyId: string,
    chapterId: string,
    commentLength: number
  ) => {
    safeLogEvent("comment_posted", {
      story_id: storyId,
      chapter_id: chapterId,
      comment_length: commentLength,
    });
  },

  /**
   * Track when a comment is added
   */
  commentAdded: (
    storyId: string | null,
    chapterId: string | null,
    authorId?: string
  ) => {
    safeLogEvent("comment_added", {
      story_id: storyId,
      chapter_id: chapterId,
      author_id: authorId,
    });
  },

  /**
   * Track when a user shares content
   */
  contentShared: (
    contentType: ContentType,
    contentId: string,
    platform: SharePlatform
  ) => {
    safeLogEvent("share", {
      content_type: contentType,
      content_id: contentId,
      method: platform,
    });
  },

  /**
   * Track reactions
   */
  reacted: (
    contentType: ContentType,
    contentId: string,
    reactionType: string
  ) => {
    safeLogEvent("reaction", {
      content_type: contentType,
      content_id: contentId,
      reaction_type: reactionType,
    });
  },
};

// Function-based aliases
export const trackFollow = trackEngagement.userFollowed;
export const trackUnfollow = trackEngagement.userUnfollowed;
export const trackCommentPost = trackEngagement.commentPosted;
export const trackShare = trackEngagement.contentShared;
export const trackReaction = trackEngagement.reacted;

// ============================================================================
// SEARCH EVENTS
// ============================================================================

/**
 * Track search queries
 */
export const trackSearch = (
  query: string,
  resultsCount: number,
  filters?: {
    genre?: Genre;
    language?: "en" | "hi";
    sortBy?: string;
  }
) => {
  safeLogEvent("search", {
    search_term: query,
    results_count: resultsCount,
    filter_genre: filters?.genre,
    filter_language: filters?.language,
    sort_by: filters?.sortBy,
  });
};

/**
 * Track when a user clicks on a search result
 */
export const trackSearchResultClick = (
  query: string,
  resultPosition: number,
  storyId: string
) => {
  safeLogEvent("search_result_click", {
    search_term: query,
    result_position: resultPosition,
    story_id: storyId,
  });
};

// ============================================================================
// ONBOARDING EVENTS
// ============================================================================

/**
 * Track onboarding progress
 */
export const trackOnboardingStep = (
  step: number,
  stepName: string,
  completed: boolean
) => {
  safeLogEvent("onboarding_step", {
    step_number: step,
    step_name: stepName,
    completed,
  });
};

/**
 * Track onboarding completion
 */
export const trackOnboardingComplete = (totalSteps: number) => {
  safeLogEvent("onboarding_complete", { total_steps: totalSteps });
};

/**
 * Track onboarding skip
 */
export const trackOnboardingSkip = (skippedAtStep: number) => {
  safeLogEvent("onboarding_skip", { skipped_at_step: skippedAtStep });
};

// ============================================================================
// ERROR EVENTS
// ============================================================================

/**
 * Track errors for debugging
 */
export const trackError = (
  error: Error | string,
  additionalInfo?: Record<string, unknown>
) => {
  const errorMessage = error instanceof Error ? error.message : error;
  const errorStack = error instanceof Error ? error.stack : undefined;

  safeLogEvent("app_error", {
    error_message: errorMessage,
    error_stack: errorStack,
    error_location:
      typeof window !== "undefined" ? window.location.pathname : "",
    ...additionalInfo,
  });
};

// ============================================================================
// PERFORMANCE EVENTS (Object-based API)
// ============================================================================

/**
 * Performance tracking object
 */
export const trackPerformance = {
  /**
   * Track page load performance metrics
   */
  pageLoad: (metricName: string, value: number) => {
    safeLogEvent("performance_metric", {
      metric_name: metricName,
      metric_value: value,
    });
  },

  /**
   * Track custom timing
   */
  timing: (category: string, variable: string, value: number) => {
    safeLogEvent("timing", {
      timing_category: category,
      timing_variable: variable,
      timing_value: value,
    });
  },
};

// ============================================================================
// REPORT EVENTS (Moderation)
// ============================================================================

/**
 * Track when a user reports content
 */
export const trackReportSubmit = (
  contentType: ContentType,
  contentId: string,
  reportReason: string
) => {
  safeLogEvent("report_submitted", {
    content_type: contentType,
    content_id: contentId,
    report_reason: reportReason,
  });
};
